import type {
  VaultEntry,
  VaultEntryInput,
  VaultEntryUpdate,
  Prompt,
  PromptInput,
  PromptUpdate,
  Ritual,
  RitualInput,
  RitualUpdate,
  Project,
  ProjectInput,
  ProjectUpdate,
  ContentType,
  EntrySummary,
  WhatsNewResult,
  PublicCounts,
  SiteSettings,
  SiteSettingsUpdate,
  MetricsSummary,
  TopPromptsResult,
  Subscriber,
  SubscribeInput,
  SubscribeResponse,
  LoginInput,
  LoginResponse,
  MeResponse,
  ApiError,
  PaginatedResult,
  ListParams,
  VaultListParams,
  SearchResponse,
  SearchParams,
} from "@akhshy/types";

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export interface ClientOptions {
  baseUrl: string;
  /** Returns the current bearer token, or null. */
  getToken?: () => string | null;
  /** Called on any 401 so the app can clear its session. */
  onUnauthorized?: () => void;
}

/**
 * Type-safe client for the akhshy.dev API. The return types are the shared
 * @akhshy/types entities, so consumers get full inference with zero drift.
 */
export function createApiClient(opts: ClientOptions) {
  const base = opts.baseUrl.replace(/\/$/, "");

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    auth = false,
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (auth) {
      const token = opts.getToken?.() ?? null;
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }
    const res = await fetch(`${base}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401) opts.onUnauthorized?.();
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const data = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      const err = (data as ApiError)?.error;
      throw new ApiClientError(
        res.status,
        err?.code ?? "error",
        err?.message ?? res.statusText,
        err?.details,
      );
    }
    return data as T;
  }

  function qs(params: Record<string, string | number | boolean | undefined>): string {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "") sp.set(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : "";
  }

  return {
    auth: {
      login: (input: LoginInput) =>
        request<LoginResponse>("POST", "/api/auth/login", input),
      me: () => request<MeResponse>("GET", "/api/auth/me", undefined, true),
    },
    vault: {
      list: (params: VaultListParams = {}) =>
        request<PaginatedResult<VaultEntry>>(
          "GET",
          `/api/vault${qs({ ...params })}`,
          undefined,
          !!params.includeUnpublished,
        ),
      get: (id: string) => request<VaultEntry>("GET", `/api/vault/${id}`),
      getBySlug: (slug: string) =>
        request<VaultEntry>("GET", `/api/vault/slug/${encodeURIComponent(slug)}`),
      related: (slug: string) =>
        request<VaultEntry[]>("GET", `/api/vault/slug/${encodeURIComponent(slug)}/related`),
      create: (input: VaultEntryInput) =>
        request<VaultEntry>("POST", "/api/vault", input, true),
      update: (id: string, patch: VaultEntryUpdate) =>
        request<VaultEntry>("PATCH", `/api/vault/${id}`, patch, true),
      remove: (id: string) => request<void>("DELETE", `/api/vault/${id}`, undefined, true),
    },
    prompts: {
      list: (params: ListParams = {}) =>
        request<PaginatedResult<Prompt>>(
          "GET",
          `/api/prompts${qs({ ...params })}`,
          undefined,
          !!params.includeUnpublished,
        ),
      get: (id: string) => request<Prompt>("GET", `/api/prompts/${id}`),
      getBySlug: (slug: string) =>
        request<Prompt>("GET", `/api/prompts/slug/${encodeURIComponent(slug)}`),
      related: (slug: string) =>
        request<Prompt[]>("GET", `/api/prompts/slug/${encodeURIComponent(slug)}/related`),
      create: (input: PromptInput) =>
        request<Prompt>("POST", "/api/prompts", input, true),
      update: (id: string, patch: PromptUpdate) =>
        request<Prompt>("PATCH", `/api/prompts/${id}`, patch, true),
      remove: (id: string) => request<void>("DELETE", `/api/prompts/${id}`, undefined, true),
    },
    rituals: {
      list: (params: ListParams = {}) =>
        request<PaginatedResult<Ritual>>(
          "GET",
          `/api/rituals${qs({ ...params })}`,
          undefined,
          !!params.includeUnpublished,
        ),
      get: (id: string) => request<Ritual>("GET", `/api/rituals/${id}`),
      getBySlug: (slug: string) =>
        request<Ritual>("GET", `/api/rituals/slug/${encodeURIComponent(slug)}`),
      related: (slug: string) =>
        request<Ritual[]>("GET", `/api/rituals/slug/${encodeURIComponent(slug)}/related`),
      create: (input: RitualInput) =>
        request<Ritual>("POST", "/api/rituals", input, true),
      update: (id: string, patch: RitualUpdate) =>
        request<Ritual>("PATCH", `/api/rituals/${id}`, patch, true),
      remove: (id: string) => request<void>("DELETE", `/api/rituals/${id}`, undefined, true),
    },
    projects: {
      list: (params: ListParams = {}) =>
        request<PaginatedResult<Project>>(
          "GET",
          `/api/projects${qs({ ...params })}`,
          undefined,
          !!params.includeUnpublished,
        ),
      get: (id: string) => request<Project>("GET", `/api/projects/${id}`),
      getBySlug: (slug: string) =>
        request<Project>("GET", `/api/projects/slug/${encodeURIComponent(slug)}`),
      related: (slug: string) =>
        request<Project[]>("GET", `/api/projects/slug/${encodeURIComponent(slug)}/related`),
      create: (input: ProjectInput) =>
        request<Project>("POST", "/api/projects", input, true),
      update: (id: string, patch: ProjectUpdate) =>
        request<Project>("PATCH", `/api/projects/${id}`, patch, true),
      remove: (id: string) => request<void>("DELETE", `/api/projects/${id}`, undefined, true),
    },
    /** Published entries of any type sharing tags with the given one. */
    relatedAcross: (type: ContentType, slug: string) =>
      request<{ items: EntrySummary[] }>(
        "GET",
        `/api/related/${type}/${encodeURIComponent(slug)}`,
      ),
    /** Reverse-chron changes across all content types, server-side paginated. */
    whatsNew: (params: ListParams = {}) =>
      request<WhatsNewResult>(
        "GET",
        `/api/whats-new${qs({ page: params.page, pageSize: params.pageSize })}`,
      ),
    /** Full-text search across vault, prompts and rituals. */
    search: (q: string, params: SearchParams = {}) =>
      request<SearchResponse>(
        "GET",
        `/api/search${qs({
          q,
          types: params.types?.join(","),
          limit: params.limit,
        })}`,
      ),
    settings: {
      get: () => request<SiteSettings>("GET", "/api/settings"),
      update: (patch: SiteSettingsUpdate) =>
        request<SiteSettings>("PATCH", "/api/settings", patch, true),
    },
    metrics: {
      /** Fire-and-forget: record that a prompt was copied. */
      recordPromptCopied: (id: string) =>
        request<void>("POST", `/api/prompts/${id}/copied`),
      /** Fire-and-forget: record that a prompt was opened in an LLM chat. */
      recordPromptLaunched: (id: string) =>
        request<void>("POST", `/api/prompts/${id}/launched`),
      /** Fire-and-forget: record that a vault link was opened. */
      recordVaultOpened: (id: string) =>
        request<void>("POST", `/api/vault/${id}/opened`),
      /** Public per-entry counts (prompt copies, vault opens). */
      counts: () => request<PublicCounts>("GET", "/api/metrics/counts"),
      /** Top copied prompts for the "most reached for" strip. */
      topPrompts: (limit?: number) =>
        request<TopPromptsResult>(
          "GET",
          `/api/metrics/prompts/top${qs({ limit })}`,
        ),
      /** Aggregate metrics snapshot for the dashboard. */
      summary: () => request<MetricsSummary>("GET", "/api/metrics/summary"),
    },
    subscribers: {
      /** Public: capture an email (single opt-in — joins the list immediately). */
      subscribe: (email: string, turnstileToken?: string) =>
        request<SubscribeResponse>("POST", "/api/subscribe", {
          email,
          ...(turnstileToken ? { turnstileToken } : {}),
        } satisfies SubscribeInput),
      /** Admin: list subscribers. */
      list: () =>
        request<{ items: Subscriber[] }>("GET", "/api/subscribe", undefined, true),
      /** Admin: unsubscribe an email. */
      unsubscribe: (email: string) =>
        request<void>("POST", "/api/subscribe/unsubscribe", { email }, true),
      /** Admin: re-add an unsubscribed email (fails if they self-unsubscribed). */
      resubscribe: (email: string) =>
        request<void>("POST", "/api/subscribe/resubscribe", { email }, true),
      /** Admin: send the latest ritual to active subscribers via Resend. */
      send: () =>
        request<{ sent: number; recipients: number; configured: boolean; message: string }>(
          "POST",
          "/api/subscribe/send",
          {},
          true,
        ),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
export type { ClientOptions as ApiClientOptions };
